import React, { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react'
import { ethers, Network } from 'ethers'
import axios from 'axios'
import { ArtData } from '@/database/art'
import { VoteData } from '@/database/vote'
import { Admin } from '@/database/admin'
import { Transaction } from '@/database/transaction'
import { Image } from '@/database/image'
import TbaNFT from '../abis/TbaNft.json'
import blockchainConfig from '../config.json'
import genericErc20Abi from '../abis/Erc20.json'
import { ArtState, TransactionActivity, TransactionStatus, TransactionType } from '@/database/types'
import { ClaimData } from '@/database/claim'
import _ from 'lodash'
import { WebSocketController } from '@/controllers/websocketcontroller'
import {
    ChatMessage,
    SpecificUpdate,
    TypingResponse,
    UpdateSpecific,
    UpdateSpecificType,
    UpdateType,
    WebSocketUpdateResponse,
} from '@/types/websocket'
import { WebSocketService } from '@/controllers/websocketservice'
import { useConnectWallet, useNotifications } from '@web3-onboard/react'
import type { WalletState } from '@web3-onboard/core'
import { custodial } from '@/utils/extras'

// const mainNetwork = {
//     chainId: 369,
//     name: 'Pulsechain Network',
//     currency: 'PLS',
//     explorerUrl: 'https://otter-pulsechain.g4mm4.io',
//     rpcUrl: 'https://rpc-pulsechain.g4mm4.io'
// }

export const mainNetwork = {
    chainId: 943,
    name: 'Pulsechain V4 Test Network',
    currency: 'tPLS',
    explorerUrl: 'https://scan.v4.testnet.pulsechain.com',
    rpcUrl: 'https://rpc.v4.testnet.pulsechain.com',
}

// export const mainNetwork = {
//     chainId: 31337,
//     name: 'Local hardhat',
//     currency: 'eth',
//     explorerUrl: 'http://127.0.0.1:8545',
//     rpcUrl: 'http://127.0.0.1:8545',
// }

// const mainNetwork = {
//     chainId: 11155111,
//     name: 'Ethereum Sepolia Test Network',
//     currency: 'ETH',
//     explorerUrl: 'https://sepolia.etherscan.io',
//     rpcUrl: 'https://eth-sepolia.g.alchemy.com/v2/9_aIJQ8GFqYVdeAJmUNVOdZPGnSejvVr'
// }

// const mainNetwork = {
//     chainId: 97,
//     name: 'BNB Smart Chain Testnet',
//     currency: 'tBNB',
//     explorerUrl: 'https://testnet.bscscan.com',
//     rpcUrl: 'https://data-seed-prebsc-1-s2.binance.org:8545/'
// }

export const metadata = {
    name: 'YourToken.Art',
    description: 'YourToken.Art CRAZY DeFi NFT Marketplace',
    url: 'https://yourtoken.art', // origin must match your domain and subdomain
    icons: ['https://yourtoken.art/'],
}

export interface UserShare {
    creator: number
    owner: number
    votes: number
    claims: number
    airdrop: number
}

export interface Tokenomics {
    artState: ArtState
    fee: number
    owner: number
    creator: number
    airdrop: number
    votes: number
    claims: number
    treasury: number
    burn: number
    burnToken: string
    burnAddress: string
    creatorAddress: string
    treasuryAddress: string
}

export type TokenomicsField = keyof Omit<
    Tokenomics,
    'artState' | 'burnToken' | 'owner' | 'burnAddress' | 'creatorAddress' | 'treasuryAddress'
>

export enum ProxyTransferFilter {
    NATIVE = 'NATIVE',
    TOKEN = 'TOKEN',
    NFT = 'NFT',
}

export interface Filters {
    forSale: boolean
    offerings: boolean
    marketMyArt: boolean
    marketMyClaims: boolean
    marketMyOffers: boolean
    auctionMyArt: boolean
    auctionMyClaims: boolean
    auctionMyBids: boolean
    forced: boolean
    activeVote: boolean
    created: boolean
    owned: boolean
    hidden: boolean
    proxyTransfer: ProxyTransferFilter
}

export type ContextType = {
    acceptBid: (artData: ArtData) => Promise<void>
    acceptOffer: (artData: ArtData) => Promise<void>
    accountSignature: string | null
    adminConfiguration: Admin
    artDataList: ArtData[]
    auctionDataList: ArtData[]
    bidHandler: (artData: ArtData, bidValue: number, previousBid: number) => Promise<void>
    categories: string[]
    claimedAmountTable: Record<number, number>
    claimedDataList: ClaimData[]
    claimHandler: (castingArtId: number, artData: ArtData, claimValue: number) => Promise<void>
    crazyPrice: string
    currentAccount: string | null
    resetWallet: () => Promise<void>
    endAuction: (tokenId: number) => Promise<void>
    faucetHandler: () => Promise<void>
    fetchImage: (imageId: number) => Promise<Image>
    filters: Filters
    getCachedImage: (imageId: number) => Image | undefined
    getTokenomics: (artData: ArtData) => Tokenomics
    hideContent: boolean
    isConnected: boolean
    loadPulseAmount: () => Promise<void>
    loadTokenAmount: () => Promise<void>
    loading: boolean
    logIn: boolean
    messages: ChatMessage[]
    offerHandler: (artData: ArtData, amount: number, expiration: number) => Promise<void>
    portfolioClaimTable: Record<number, number>
    portfolioData: ArtData[]
    proposeHandler: (artData: ArtData) => Promise<void>
    proposalData: ArtData[]
    proxyTransferToken: (personalWallet: string, address: string, recipient: string, amount: string) => Promise<void>
    proxyTransferNative: (personalWallet: string, recipient: string, amount: string) => Promise<void>
    proxyTransferNft: (personalWallet: string, address: string, tokenId: number, recipient: string) => Promise<void>
    pulseAmount: string
    pulsePrice: string
    remove: (artId: number) => Promise<void>
    saleHandler: (artData: ArtData, amount: string, expiration: number) => Promise<void>
    setActiveSpecificUpdates: Dispatch<SetStateAction<SpecificUpdate>>
    setActiveUpdates: Dispatch<SetStateAction<UpdateType[]>>
    setHideContent: Dispatch<SetStateAction<boolean>>
    setLoading: Dispatch<SetStateAction<boolean>>
    socketId: string | null
    tokenAmount: string
    toggleHideArt: (artData: ArtData) => Promise<void>
    toggleFilter: (filter: keyof Filters, filterChoice?: ProxyTransferFilter) => void
    totalClaimPower: string
    transactionTokenArray: Transaction[][]
    transactionUserArray: Transaction[][]
    transferClaim: (personalWallet: string, address: string, recipient: string, amount: string) => Promise<void>
    transferNft: (artData: ArtData, receiverAddress: string, transferFee: string) => Promise<string>
    typingStatus: string
    walletController: () => {
        wallet: WalletState | null
        signMessage: (wallet: WalletState, message: string) => Promise<string>
        connectWallet: () => Promise<void>
        disconnectWallet: () => Promise<void>
        account: string
        isConnected: boolean
    }
    voteHandler: (artData: ArtData, voteValue: number) => Promise<void>
    votePower: string
    votedDataList: VoteData[]
    walletProvider: ethers.Eip1193Provider | undefined
    webSocketService: WebSocketService
}

const emptyWalletState = {
    wallet: null as WalletState | null,
    signMessage: async () => '',
    connectWallet: async () => {},
    disconnectWallet: async () => {},
    account: '',
    isConnected: false,
}

// Create the context with default values
export const Context = createContext<ContextType>({
    acceptBid: () => Promise.resolve(),
    acceptOffer: () => Promise.resolve(),
    adminConfiguration: {} as Admin,
    artDataList: [] as ArtData[],
    auctionDataList: [] as ArtData[],
    accountSignature: '',
    bidHandler: () => Promise.resolve(),
    categories: [],
    claimedAmountTable: {} as Record<number, number>,
    claimedDataList: [] as ClaimData[],
    claimHandler: () => Promise.resolve(),
    crazyPrice: ' - ',
    currentAccount: '',
    resetWallet: () => Promise.resolve(),
    endAuction: () => Promise.resolve(),
    faucetHandler: () => Promise.resolve(),
    fetchImage: () => Promise.resolve({} as Image),
    filters: {} as Filters,
    getCachedImage: () => undefined,
    getTokenomics: (): Tokenomics => {
        return {} as Tokenomics
    },
    hideContent: false,
    isConnected: false,
    loadPulseAmount: () => Promise.resolve(),
    loadTokenAmount: () => Promise.resolve(),
    loading: false,
    logIn: false,
    messages: [] as ChatMessage[],
    offerHandler: () => Promise.resolve(),
    portfolioClaimTable: {} as Record<number, number>,
    portfolioData: [] as ArtData[],
    proposeHandler: () => Promise.resolve(),
    proposalData: [] as ArtData[],
    proxyTransferToken: () => Promise.resolve(),
    proxyTransferNative: () => Promise.resolve(),
    proxyTransferNft: () => Promise.resolve(),
    pulseAmount: ' - ',
    pulsePrice: ' - ',
    remove: () => Promise.resolve(),
    saleHandler: () => Promise.resolve(),
    setActiveUpdates: {} as Dispatch<SetStateAction<UpdateType[]>>,
    setHideContent: () => {},
    setLoading: () => {},
    setActiveSpecificUpdates: {} as Dispatch<SetStateAction<SpecificUpdate>>,
    socketId: '',
    tokenAmount: '',
    toggleHideArt: () => Promise.resolve(),
    toggleFilter: () => {},
    transactionTokenArray: [],
    transactionUserArray: [],
    transferClaim: () => Promise.resolve(),
    transferNft: () => Promise.resolve({} as string),
    typingStatus: '',
    totalClaimPower: '-',
    walletController: () => emptyWalletState,
    voteHandler: () => Promise.resolve(),
    votePower: '-',
    votedDataList: [] as VoteData[],
    walletProvider: {} as ethers.Eip1193Provider,
    webSocketService: {} as WebSocketService,
})

const generateClaimedAmountTable = (claimedDataArray: ClaimData[]): Record<number, number> =>
    claimedDataArray.reduce(
        (record, claimData) => {
            const tokenId = claimData.targetArt.nft.tokenId
            const amount = parseInt(claimData.claim.amount)
            record[tokenId] = (record[tokenId] || 0) + amount
            return record
        },
        {} as Record<number, number>,
    )

const generatePortfolioClaimTable = (portfolioData: ArtData[]): Record<number, number> =>
    portfolioData.reduce(
        (record, artData) => {
            if (artData.tokenId !== undefined) {
                const artId = artData.art.id
                record[artId] = (record[artId] || 0) + artData.claimPower - artData.totalClaims
            }
            return record
        },
        {} as Record<number, number>,
    )

const calcTotalVotedAmount = (voteDataArray: VoteData[]): number => {
    return voteDataArray.reduce((total: number, voteData) => {
        if (voteData.art.state === ArtState.FORCED_AUCTION || voteData.art.state === ArtState.CREATED) {
            const amount = parseFloat(voteData.vote.amount ? voteData.vote.amount : '0')
            total += amount
        }
        return total
    }, 0)
}

const chainConfig = (network: Network) => blockchainConfig[Number(network.chainId)]

const ContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [{ wallet }, connect, disconnect] = useConnectWallet()
    const [notifications] = useNotifications()

    const wsController = WebSocketController.getInstance()
    const webSocketService = wsController.webSocketService
    const storedFilters = typeof window !== 'undefined' && localStorage.getItem('filters')

    const [adminConfiguration, setAdminConfiguration] = useState({} as Admin)
    const [categories, setCategories] = useState([])
    const [currentAccount, setCurrentAccount] = useState('')
    const [accountSignature, setAccountSignature] = useState('')
    const [tokenAmount, setTokenAmount] = useState('-')
    const [pulseAmount, setPulseAmount] = useState('-')
    const [votePower, setVotePower] = useState('-')
    const [totalClaimPower, setTotalClaimPower] = useState('-')
    const [loading, setLoading] = useState(false)
    const [logIn, setLogIn] = useState(false)
    const [hideContent, setHideContent] = useState(false)
    const [portfolioData, setPortfolioData] = useState<ArtData[]>([])
    const [proposalData, setProposalData] = useState<ArtData[]>([])
    const [artDataList, setArtDataList] = useState<ArtData[]>([])
    const [imageList, setImageList] = useState<Image[]>([])
    const [auctionDataList, setAuctionDataList] = useState<ArtData[]>([])
    const [votedDataList, setVotedDataList] = useState<VoteData[]>([])
    const [claimedDataList, setClaimedDataList] = useState<ClaimData[]>([])
    const [claimedAmountTable, setClaimedAmountTable] = useState<Record<number, number>>({}) // Lookup table for finding the total claim amount used per asset
    const [crazyPrice, setCrazyPrice] = useState(' - ')
    const [portfolioClaimTable, setPortfolioClaimTable] = useState<Record<number, number>>({})
    const [isConnected, setIsConnected] = useState(false)
    const [walletProvider, setWalletProvider] = useState({} as ethers.Eip1193Provider)
    const [messages, setMessages] = useState([] as ChatMessage[])
    const [transactionTokenArray, setTransactionTokenArray] = useState<Transaction[][]>([])
    const [transactionUserArray, setTransactionUserArray] = useState<Transaction[][]>([])
    const [pulsePrice, setPulsePrice] = useState(' - ')
    const [typingStatus, setTypingStatus] = useState('')
    const [socketId, setSocketId] = useState<string>('0')
    const [updateTypes, setUpdateTypes] = useState<UpdateType[]>([] as UpdateType[])
    const [specificUpdates, setSpecificUpdates] = useState({
        updates: [],
    } as SpecificUpdate)
    const [currentFetchTimes, setCurrentFetchTimes] = useState({})
    const [lastFetchTimes, setLastFetchTimes] = useState({})
    const [activeUpdates, setActiveUpdates] = useState([] as UpdateType[])
    const [activeSpecificUpdates, setActiveSpecificUpdates] = useState({
        updates: [],
    } as SpecificUpdate)
    const [filters, setFilters] = useState<Filters>(
        storedFilters
            ? (JSON.parse(storedFilters) as Filters)
            : {
                  forSale: false,
                  offerings: false,
                  marketMyArt: false,
                  marketMyClaims: false,
                  marketMyOffers: false,
                  auctionMyArt: false,
                  auctionMyClaims: false,
                  auctionMyBids: false,
                  forced: false,
                  activeVote: true,
                  created: false,
                  owned: true,
                  hidden: false,
                  proxyTransfer: ProxyTransferFilter.NATIVE,
              },
    )
    //    const [wallet, setWallet] = useState<WalletState | null>(null)
    const account = wallet ? wallet.accounts[0]?.address?.toLowerCase() : ''

    const toggleFilter = (filter: keyof Filters, filterRadio?: ProxyTransferFilter | undefined) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [filter]: filterRadio ?? !prevFilters[filter],
        }))
    }

    useEffect(() => {
        console.log(notifications)
    }, [notifications])

    const deleteIndexedDBDatabases = (): void => {
        const dbsToDelete = ['walletconnect', 'WALLET_CONNECT_V2_INDEXED_DB']
        for (const dbName of dbsToDelete) {
            try {
                indexedDB.deleteDatabase(dbName)
                console.log(`Deleted IndexedDB: ${dbName}`)
            } catch (e) {
                console.warn(`Could not delete IndexedDB: ${dbName}`, e)
            }
        }
    }

    const walletController = () => {
        const account = wallet ? wallet.accounts[0]?.address?.toLowerCase() : ''

        const connectWallet = async () => {
            setCurrentAccount('')
            try {
                const wallets = await connect()
                if (wallets[0] != null) {
                    setLogIn(true)
                    setWalletProvider(wallets[0].provider)
                    setIsConnected(true)
                }
            } catch (error) {
                console.error('Error connecting wallet:', error)
            }
        }

        const disconnectWallet = async () => {
            deleteIndexedDBDatabases()
            if (wallet) {
                await resetWallet()
                await disconnect({ label: wallet.label })
                if (typeof indexedDB !== 'undefined') {
                    try {
                        indexedDB.deleteDatabase('walletconnect')
                    } catch {
                        /* ignore */
                    }
                }
                Object.keys(localStorage).forEach((key) => {
                    if (key.startsWith('wc@2:') || key.startsWith('@appkit/') || key.startsWith('walletconnect')) {
                        localStorage.removeItem(key)
                        console.log(`Removed localStorage key: ${key}`)
                    }
                })
                console.log('Disconnected wallet')
            }
        }

        const signMessage = async (wallet: WalletState | null, message: string) => {
            // Set up Ether provider using the connected wallet provider
            const walletProvider = wallet?.provider
            if (!walletProvider) {
                return ''
            }

            const ethersProvider = new ethers.BrowserProvider(walletProvider)
            // Get the signer from the provider

            try {
                // Sign the message
                return await ethersProvider.send('personal_sign', [message, account])
            } catch (error) {
                console.error('Error signing message:', error)
                setCurrentAccount('')
                setAccountSignature('')
                return ''
            }
        }

        const renewAccountSignature = async (wallet: WalletState) => {
            try {
                const message = `YourToken.art account signature for ${account},${Date.now()} which will expire in 48h`
                signMessage(wallet, message).then((signature) => {
                    axios
                        .post('/renew-account-signature/', {
                            signature: signature,
                            message: message,
                            account: account,
                        })
                        .then((result) => {
                            if (result.status === 200) {
                                localStorage.setItem('account', account)
                                localStorage.setItem(`account_signature_${account}`, signature)
                                setAccountSignature(signature)
                                setCurrentAccount(account)
                            } else {
                                localStorage.removeItem(`account_signature_${account}`)
                                setCurrentAccount('')
                                disconnectWallet()
                            }
                        })
                })
            } catch (error) {
                localStorage.removeItem(`account_signature_${account}`)
                console.error('Error creating account signature', error)
                setCurrentAccount('')
                setAccountSignature('')
                await disconnectWallet()
            }
        }

        return {
            wallet,
            signMessage,
            connectWallet,
            disconnectWallet,
            renewAccountSignature,
            account,
            isConnected: !!wallet,
        }
    }

    const resetWallet = async () => {
        setIsConnected(false)
        setCurrentAccount('')
        setAccountSignature('')
        setTokenAmount('-')
        setTotalClaimPower('-')
        setLogIn(false)
    }

    const loadTokenAmount = async () => {
        if (walletProvider && isConnected && currentAccount && accountSignature) {
            const provider = new ethers.BrowserProvider(walletProvider)
            const network = await provider.getNetwork()
            const signer = await provider.getSigner()
            const signerAddress = (await signer.getAddress()).toLowerCase()
            try {
                const contract = new ethers.Contract(chainConfig(network).token.address, genericErc20Abi, signer)
                const balance = await contract.balanceOf(signerAddress)
                const balanceInEther = ethers.formatEther(balance)
                const formattedBalance = parseFloat(balanceInEther).toFixed(2)
                setTokenAmount(formattedBalance)
            } catch (error) {
                setTokenAmount('')
                const network = await provider.getNetwork()
                console.log('Token not found on network ' + network.chainId)
                console.log('Error fetching token:', error)
            }
        }
    }

    const loadPulseAmount = async () => {
        if (walletProvider) {
            //            const provider = new ethers.providers.Web3Provider(walletProvider);
            const provider = new ethers.BrowserProvider(walletProvider)
            //            const provider = new ethers.providers.JsonRpcProvider(rpcUrl);

            const signer = await provider.getSigner()
            const signerAddress = (await signer.getAddress()).toLowerCase()
            const balance = await provider.getBalance(signerAddress)
            const balanceInEther = ethers.formatEther(balance)
            const formattedBalance = parseFloat(balanceInEther).toFixed(2)
            setPulseAmount(formattedBalance)
        }
    }

    // Convert the balance from wei to ether (or the base unit of the native token)

    const handleUpdateUser = async () => {
        const userPayload = {
            account: currentAccount,
            signature: accountSignature,
            walletAddress: currentAccount,
            crazyBalance: tokenAmount,
        }
        if (accountSignature && currentAccount && tokenAmount) {
            await axios.post('/users/', userPayload)
        }
    }

    const endAuction = async (tokenId: number) => {
        try {
            await axios.post('/auction-end/', {
                account: currentAccount,
                signature: accountSignature,
                tokenId: tokenId,
            })
        } catch (error) {
            console.error('Error ending auction:', error)
        }
    }

    const faucetHandler = async () => {
        try {
            const response = await axios.get('/faucet/' + currentAccount)
            console.log('Faucet: ' + JSON.stringify(response))
        } catch (error) {
            console.error('Error requesting faucet', error)
        }
    }

    const fetchAdminConfiguration = async () => {
        try {
            const response = await axios.get('/admin/')
            const admin: Admin = response.data
            setAdminConfiguration(admin)
        } catch (error) {
            console.error('Error fetching admin configuration:', error)
        }
    }

    const fetchAllArt = async (forced: boolean) => {
        if (forced || !artDataList) {
            try {
                const response = await axios.get('/art/')
                setArtDataList(response.data)
            } catch (error) {
                console.error('Error fetching all art:', error)
            }
        }
    }

    const fetchCategories = async () => {
        if (categories?.length) return
        try {
            const response = await axios.get('/categories/')
            setCategories(response.data)
        } catch (error) {
            console.error('Error fetching categories:', error)
        }
    }

    const fetchImage = async (imageId: number) => {
        const cachedImage = imageList.find((image) => image.id === imageId)
        if (cachedImage) {
            return cachedImage
        }
        try {
            const response = await axios.get('/image/' + imageId)
            const fetchImage = response.data as Image
            setImageList((prevList) => [...prevList, fetchImage])
            return fetchImage
        } catch (error) {
            console.error('Error fetch image:', error)
            return {} as Image
        }
    }

    const getCachedImage = (imageId: number): Image | undefined => {
        return imageList.find((image) => image.id === imageId)
    }

    const fetchTokenTransactions = async (tokenId: number | string, forced: boolean) => {
        if (forced || !transactionTokenArray[tokenId]) {
            try {
                const response = await axios.get(`/transactions/${tokenId}`)

                setTransactionTokenArray((prevState) => {
                    const newState = [...prevState]
                    newState[tokenId as number] = response.data as Transaction[]
                    return newState
                })
            } catch (error) {
                console.error('Error fetching all transactions:', error)
            }
        }
    }

    const fetchUserTransactions = async (user: string | number, forced: boolean) => {
        if (user) {
            if (forced || !transactionUserArray[user]) {
                try {
                    const response = await axios.get(`/transactions-user/${user}`)
                    setTransactionUserArray((prevState) => {
                        const newState = [...prevState]
                        newState[user as string] = response.data as Transaction[]
                        return newState
                    })
                } catch (error) {
                    console.error('Error fetching all transactions:', error)
                }
            }
        }
    }

    const acceptBid = async (artData: ArtData) => {
        try {
            const message = `Accepting bid for token ID ${artData.tokenId}`
            const signature = await walletController().signMessage(wallet, message)

            await axios.post('/accept-bid/', {
                tokenId: artData.tokenId,
                currentAccount: currentAccount,
                signature: signature,
                message: message,
            })
        } catch (error) {
            console.error('Error accepting highest bid:', error)
        }
    }

    const acceptOffer = async (artData: ArtData) => {
        try {
            const message = `Accepting offer for token ID ${artData.tokenId}`
            const signature = await walletController().signMessage(wallet, message)

            const transferResult = await transferNftWithoutTax(artData)
            if (transferResult) {
                await axios.post('/accept-offer/', {
                    tokenId: artData.tokenId,
                    currentAccount: currentAccount,
                    signature: signature,
                    message: message,
                })
            } else {
                console.error('Error sending NFT to custodial wallet and accepting highest offer:')
            }
        } catch (error) {
            console.error('Error accepting highest offer:', error)
        }
    }

    const fetchAllAuctions = async (forced: boolean) => {
        if (forced || !auctionDataList) {
            try {
                const response = await axios.get('/auctions/')
                const artData: ArtData[] = response.data
                setAuctionDataList(artData)
            } catch (error) {
                console.error('Error fetching all auctions:', error)
            }
        }
    }

    const fetchVotes = async (forced?: boolean) => {
        if (currentAccount && accountSignature) {
            if (forced || !votedDataList) {
                try {
                    const response = await axios.get('/votes/' + currentAccount)
                    const voteData: VoteData[] = response.data
                    setVotedDataList(voteData.toReversed())
                } catch (error) {
                    console.error('Error fetching user votes:', error)
                }
            }
        } else {
            setVotedDataList([])
        }
    }

    const fetchClaims = async (forced?: boolean) => {
        if (currentAccount && accountSignature) {
            if (forced || !claimedDataList) {
                try {
                    const response = await axios.get('/claims/' + currentAccount)
                    const claimData: ClaimData[] = response.data
                    setClaimedDataList(claimData.toReversed())
                } catch (error) {
                    console.error('Error fetching user votes:', error)
                }
            }
        } else {
            setClaimedDataList([])
        }
    }

    // Bid on NFTs
    const bidHandler = async (artData: ArtData, bidAmount: number, previousBid = 0) => {
        setLoading(true)
        try {
            // Interact contract as signer
            const provider = new ethers.BrowserProvider(walletProvider)
            const signer = await provider.getSigner()
            const network = await provider.getNetwork()

            const erc20Contract = new ethers.Contract(chainConfig(network).token.address, genericErc20Abi, signer)

            // Calculate the price in ERC20 tokens and approve the transfer
            const bidIncrease = ethers.parseUnits((bidAmount - previousBid).toString(), 18)

            // Transfer the ERC20 tokens to the marketplace custodial wallet
            const transferTx = await erc20Contract.transfer(
                chainConfig(network).custodial.address.toLowerCase(),
                bidIncrease,
            )
            const receipt = await transferTx.wait()
            console.log('Transfer receipt:', signer)
            console.log('Transfer receipt:', JSON.stringify(receipt))

            // Once the token transfer is successful, notify the server to transfer the NFT.
            // Define a function to attempt sending the receipt to your server
            const attemptToSendReceipt = async (attempt = 1) => {
                try {
                    await axios.post('/bid-on-nft/', {
                        account: currentAccount,
                        signature: accountSignature,
                        amount: bidAmount,
                        bidIncrease: (bidAmount - previousBid).toFixed(2),
                        sender: (await signer.getAddress()).toLowerCase(),
                        receiver: chainConfig(network).custodial.address.toLowerCase(),
                        artId: artData.art.id,
                        receipt: JSON.stringify(receipt),
                    })
                } catch (error) {
                    if (attempt <= 5) {
                        // Retry up to 5 times
                        console.warn(`Attempt ${attempt}: Retrying to send receipt...`, error?.response?.data)
                        await new Promise((resolve) => setTimeout(resolve, 2000)) // Wait 2 seconds
                        return attemptToSendReceipt(attempt + 1)
                    } else {
                        console.error('Failed to send receipt to the server after several attempts.')
                        throw error
                    }
                }
            }

            await attemptToSendReceipt()
        } catch (error) {
            console.error('Error during bid handling:', error)
        } finally {
            setLoading(false)
        }
    }

    const offerHandler = async (artData: ArtData, amount: number, expiration: number) => {
        setLoading(true)
        // Interact contract as signer
        const provider = new ethers.BrowserProvider(walletProvider)
        const signer = await provider.getSigner()
        const network = await provider.getNetwork()
        const erc20Contract = new ethers.Contract(chainConfig(network).token.address, genericErc20Abi, signer)

        // Calculate the price in ERC20 tokens
        const price = ethers.parseUnits(amount > 0 ? amount.toString() : '0', 'ether')
        // Transfer the ERC20 tokens to the NFT creator
        const transferTx = await erc20Contract.transfer(chainConfig(network).custodial.address.toLowerCase(), price)
        const receipt = await transferTx.wait()

        // Once the token transfer is successful, notify the server to transfer the NFT.
        // Define a function to attempt sending the receipt to your server
        const attemptToSendReceipt = async (attempt = 1) => {
            try {
                await axios.post('/offer-on-nft/', {
                    account: currentAccount,
                    signature: accountSignature,
                    sender: (await signer.getAddress()).toLowerCase(),
                    receiver: chainConfig(network).custodial.address.toLowerCase(),
                    artId: artData.art.id,
                    amount: amount > 0 ? amount.toString() : '0',
                    expirationInHours: expiration,
                    receipt: JSON.stringify(receipt),
                })
            } catch (error) {
                if (attempt <= 5) {
                    // Retry up to 5 times
                    console.log(JSON.stringify(error?.response?.data))
                    console.log(`Attempt ${attempt}: Retrying to send receipt...`)
                    setTimeout(() => attemptToSendReceipt(attempt + 1), 2000) // Wait 2 seconds before retrying
                } else {
                    console.error('Failed to send receipt to the server after several attempts.')
                }
            }
        }

        await attemptToSendReceipt()
        setLoading(false)
    }

    const sendTransaction = async (
        type: TransactionType,
        status: TransactionStatus,
        senderId: number,
        sender: string,
        receiverId: number,
        receiver: string,
        artId: number,
        owner: string,
        receipt: string,
        activity: TransactionActivity,
        expiration = new Date(Date.now()),
        amount = '-1',
    ): Promise<string> => {
        const errorMessage = 'Failed to send transaction to the server after several attempts.'
        const attemptToSend = async (attempt = 1) => {
            try {
                await axios.post('/transaction/', {
                    account: currentAccount,
                    signature: accountSignature,
                    amount: amount,
                    type: type,
                    status: status,
                    expiration: expiration,
                    activity: activity,
                    senderId: senderId,
                    sender: sender,
                    receiverId: receiverId,
                    receiver: receiver,
                    artId: artId,
                    owner: owner,
                    receipt: receipt,
                })
                return ''
            } catch (error) {
                if (attempt <= 5) {
                    // Retry up to 5 times
                    console.log(JSON.stringify(error?.response?.data))
                    console.log(`Attempt ${attempt}: Retrying to send ...`)
                    setTimeout(() => attemptToSend(attempt + 1), 2000) // Wait 2 seconds before retrying
                } else {
                    console.error(errorMessage)
                    return errorMessage
                }
            }
            return errorMessage
        }
        return await attemptToSend()
    }

    const transferClaim = async (personalWallet: string, tokenAddress: string, recipient: string, amount: string) => {
        const provider = new ethers.BrowserProvider(walletProvider)
        const signer = await provider.getSigner()
        const network = await provider.getNetwork()
        if (!signer) {
            throw new Error('Wallet not connected')
        }
        // Check if the wallet is connected
        if (!currentAccount || !provider) {
            throw new Error('Wallet not connected')
        }

        try {
            // ERC20 interface for token approval
            const tokenContract = new ethers.Contract(
                tokenAddress,
                ['function approve(address spender, uint256 amount) public returns (bool)'],
                signer,
            )

            // Your proxy contract address
            const proxyContractAddress = chainConfig(network).nft.address

            // The proxy contract interface
            const proxyContract = new ethers.Contract(proxyContractAddress, TbaNFT.abi, signer)

            // First approve the proxy contract to spend tokens
            const amountWei = ethers.parseUnits(amount, 18) // Adjust decimals if needed

            // This will trigger the Rabby wallet popup for approval
            const approveTx = await tokenContract.approve(proxyContractAddress, amountWei)
            await approveTx.wait()

            // Now call the proxy transfer function
            const transferTx = await proxyContract.proxyTransferToken(
                personalWallet.toLowerCase(),
                tokenAddress.toLowerCase(),
                recipient.toLowerCase(),
                amountWei,
            )
            await transferTx.wait()
        } catch (error) {
            console.error('Proxy transfer failed:', error)
            throw error
        }
    }

    const proxyTransferToken = async (
        personalWallet: string,
        tokenAddress: string,
        recipient: string,
        amount: string,
    ) => {
        const provider = new ethers.BrowserProvider(walletProvider)
        const signer = await provider.getSigner()
        const network = await provider.getNetwork()
        if (!signer) {
            throw new Error('Wallet not connected')
        }
        // Check if the wallet is connected
        if (!currentAccount || !provider) {
            throw new Error('Wallet not connected')
        }

        try {
            // ERC20 interface for token approval
            // const tokenContract = new ethers.Contract(
            //     tokenAddress,
            //     ['function approve(address spender, uint256 amount) public returns (bool)'],
            //     signer,
            // )

            // Your proxy contract address
            const proxyContractAddress = chainConfig(network).nft.address

            // The proxy contract interface
            const proxyContract = new ethers.Contract(proxyContractAddress, TbaNFT.abi, signer)

            // First approve the proxy contract to spend tokens
            const amountWei = ethers.parseUnits(amount, 18) // Adjust decimals if needed

            // // This will trigger the Rabby wallet popup for approval
            // const approveTx = await tokenContract.approve(proxyContractAddress, amountWei)
            // await approveTx.wait()

            // Now call the proxy transfer function
            const transferTx = await proxyContract.proxyTransferToken(
                personalWallet.toLowerCase(),
                tokenAddress.toLowerCase(),
                recipient.toLowerCase(),
                amountWei,
            )
            return await transferTx.wait()
        } catch (error) {
            console.error('Proxy transfer failed:', error)
            throw error
        }
    }

    const proxyTransferNative = async (personalWallet: string, recipient: string, amount: string) => {
        const provider = new ethers.BrowserProvider(walletProvider)
        const signer = await provider.getSigner()
        const network = await provider.getNetwork()
        if (!signer) {
            throw new Error('Wallet not connected')
        }
        // Check if the wallet is connected
        if (!currentAccount || !provider) {
            throw new Error('Wallet not connected')
        }

        try {
            // Your proxy contract address
            const proxyContractAddress = chainConfig(network).nft.address

            // The proxy contract interface
            const proxyContract = new ethers.Contract(proxyContractAddress, TbaNFT.abi, signer)

            // Verify ownership of the personal wallet
            const owner = await proxyContract.ownerOf(1)
            console.log('Owner of personal wallet:', owner)
            if (owner.toLowerCase() !== currentAccount) {
                throw new Error('You are not the owner of this wallet')
            }

            // First approve the proxy contract to spend tokens
            const amountWei = ethers.parseUnits(amount, 18) // Adjust decimals if needed

            // Now call the proxy transfer function
            const transferTx = await proxyContract.proxyTransferETH(personalWallet, recipient, amountWei)
            return await transferTx.wait()
        } catch (error) {
            console.error('Proxy transfer failed:', error)
            throw error
        }
    }

    const proxyTransferNft = async (personalWallet: string, nftAddress: string, tokenId: number, recipient: string) => {
        const provider = new ethers.BrowserProvider(walletProvider)
        const signer = await provider.getSigner()
        const network = await provider.getNetwork()
        if (!signer) {
            throw new Error('Wallet not connected')
        }
        // Check if the wallet is connected
        if (!currentAccount || !provider) {
            throw new Error('Wallet not connected')
        }

        try {
            // ERC721 interface
            const nftContract = new ethers.Contract(
                nftAddress,
                [
                    'function approve(address to, uint256 tokenId) external',
                    'function getApproved(uint256 tokenId) view returns (address)',
                ],
                signer,
            )

            // Your proxy contract address
            const proxyContractAddress = chainConfig(network).nft.address

            // Optional: check if already approved
            const approvedAddress = await nftContract.getApproved(tokenId)
            if (approvedAddress.toLowerCase() !== proxyContractAddress.toLowerCase()) {
                const approveTx = await nftContract.approve(proxyContractAddress, tokenId)
                await approveTx.wait()
            }

            // The proxy contract interface
            const proxyContract = new ethers.Contract(proxyContractAddress, TbaNFT.abi, signer)

            // Now call the proxy transfer function
            const transferTx = await proxyContract.proxyTransferNFT(personalWallet, nftAddress, recipient, tokenId)
            return await transferTx.wait()
        } catch (error) {
            console.error('Proxy transfer failed:', JSON.stringify(error))
            throw error
        }
    }

    const findRevertReason = (structLogs: any[], memory: string[]): string => {
        const revertIdx = [...structLogs].reverse().findIndex((log) => log.op === 'REVERT')
        if (revertIdx === -1) return 'No REVERT found in trace'

        const i = structLogs.length - 1 - revertIdx
        const log = structLogs[i - 1]
        const offset = parseInt(log.stack.at(-1), 16)
        const length = parseInt(log.stack.at(-2), 16)

        const raw = memory.slice(offset * 2, (offset + length + 4) * 2).join('')
        const reasonHex = '0x' + raw.slice(8) // skip selector 0x08c379a0
        try {
            const decoded = ethers.AbiCoder.defaultAbiCoder().decode(['string'], reasonHex)
            return decoded[0]
        } catch {
            return 'Unable to decode revert reason'
        }
    }

    const transferNft = async (artData: ArtData, receiverAddress: string, transferFee: string): Promise<string> => {
        const errorPrefix = 'Transfer NFT error: '
        const provider = new ethers.BrowserProvider(walletProvider)
        const signer = await provider.getSigner()
        const sender = (await signer.getAddress()).toLowerCase()
        const network = await provider.getNetwork()
        const nftAddress = chainConfig(network).nft.address
        const feeTokenAddress = chainConfig(network).token.address

        // Convert transferFee to BigInt
        const feeWei = ethers.parseEther(transferFee)
        // Approve ERC20 token spending for the transfer fee
        const erc20Abi = [
            'function allowance(address,address) view returns (uint256)',
            'function approve(address,uint256) returns (bool)',
            'function balanceOf(address) view returns (uint256)',
        ]
        const feeToken = new ethers.Contract(feeTokenAddress, erc20Abi, signer)
        const me = (await signer.getAddress()).toLowerCase()
        const allowance = await feeToken.allowance((await signer.getAddress()).toLowerCase(), nftAddress)
        if (allowance < feeWei) {
            const approvalTx = await feeToken.approve(nftAddress, feeWei)
            await approvalTx.wait()
        }

        const balance = await feeToken.balanceOf(me)
        console.log('balance:', balance.toString())
        if (balance < feeWei) {
            throw new Error(errorPrefix + `Insufficient balance. Have ${balance}, need ${feeWei}`)
        }

        const nft = new ethers.Contract(nftAddress, TbaNFT.abi, signer)

        // prepare interface for parsing errors
        const iface = new ethers.Interface(TbaNFT.abi)
        let txHash
        try {
            // Now call the proxy transfer function

            const transferTx =
                sender === custodial
                    ? await nft.transferWithoutTax(sender, receiverAddress, artData.tokenId, {
                          gasLimit: 1000000n,
                      })
                    : await nft.transferWithTax(receiverAddress, artData.tokenId, feeWei, {
                          gasLimit: 1000000n,
                      })
            const receipt = await transferTx.wait()
            txHash = transferTx.hash
            await sendTransaction(
                TransactionType.TRANSFER_NFT,
                TransactionStatus.TRANSFERRED,
                -1,
                sender,
                -1,
                receiverAddress,
                artData.art.id,
                artData.currentOwner,
                JSON.stringify(receipt),
                TransactionActivity.ACCEPTED,
                undefined,
                feeWei.toString(),
            )
            return transferTx.hash
        } catch (rawError: any) {
            let userMessage = errorPrefix
            // peel off the actual revert data
            const data = rawError.data?.data ?? rawError.error?.data ?? rawError.data
            try {
                if (txHash) {
                    const localProvider = new ethers.JsonRpcProvider('http://localhost:8545')
                    const trace = await localProvider.send('debug_traceTransaction', [txHash])
                    const reason = findRevertReason(trace.structLogs, trace.structLogs.at(-1)?.memory ?? [])
                    userMessage += `Revert Reason: ${reason}`
                }
            } catch (error) {
                userMessage += 'Trace failed: ' + (error as Error).message
                console.error('Trace failed:', userMessage)
            }
            if (data) {
                try {
                    const decoded = iface.parseError(data)
                    const args = decoded?.args.map((a) => a.toString()).join(', ')
                    userMessage += `${decoded?.name}(${args})`
                } catch {
                    // Try parsing require(string) error
                    if (data?.startsWith('0x08c379a0')) {
                        const reason = ethers.AbiCoder.defaultAbiCoder().decode(['string'], '0x' + data.slice(10))
                        userMessage += `Error(${reason})`
                    } else {
                        userMessage += rawError.message ?? JSON.stringify(rawError)
                    }
                }
            } else {
                userMessage += rawError.message ?? JSON.stringify(rawError)
            }

            await sendTransaction(
                TransactionType.TRANSFER_NFT,
                TransactionStatus.ERROR,
                -1,
                sender,
                -1,
                chainConfig(network).custodial.address.toLowerCase(),
                artData.art.id,
                artData.currentOwner,
                JSON.stringify(userMessage),
                TransactionActivity.INACTIVE,
            )
            window.alert(userMessage)
            console.log(userMessage)
            return ''
        }
    }

    const transferNftWithoutTax = async (artData: ArtData): Promise<string> => {
        const errorPrefix = 'Transfer NFT error: '
        const provider = new ethers.BrowserProvider(walletProvider)
        const signer = await provider.getSigner()
        const network = await provider.getNetwork()
        const nftAddress = chainConfig(network).nft.address
        const sender = (await signer.getAddress()).toLowerCase()
        const nft = new ethers.Contract(nftAddress, TbaNFT.abi, signer)
        const receiverAddress = chainConfig(network).custodial.address.toLowerCase()

        // prepare interface for parsing errors
        const iface = new ethers.Interface(TbaNFT.abi)

        try {
            // Now call the proxy transfer function
            const transferTx = await nft.transferWithoutTax(sender, receiverAddress, artData.tokenId)
            const receipt = await transferTx.wait()
            await sendTransaction(
                TransactionType.TRANSFER_NFT,
                TransactionStatus.TRANSFERRED,
                -1,
                sender,
                -1,
                receiverAddress,
                artData.art.id,
                artData.currentOwner,
                JSON.stringify(receipt),
                TransactionActivity.ACCEPTED,
                undefined,
                '0',
            )
            return transferTx.hash
        } catch (rawError: any) {
            let userMessage = errorPrefix
            // peel off the actual revert data
            const data = rawError.data?.data ?? rawError.error?.data ?? rawError.data
            if (data) {
                try {
                    const decoded = iface.parseError(data)
                    const args = decoded?.args.map((a) => a.toString()).join(', ')
                    userMessage += `${decoded?.name}(${args})`
                } catch {
                    userMessage += rawError.message ?? JSON.stringify(rawError)
                }
            } else {
                userMessage += rawError.message ?? JSON.stringify(rawError)
            }

            await sendTransaction(
                TransactionType.TRANSFER_NFT,
                TransactionStatus.ERROR,
                -1,
                sender,
                -1,
                chainConfig(network).custodial.address.toLowerCase(),
                artData.art.id,
                artData.currentOwner,
                JSON.stringify(userMessage),
                TransactionActivity.INACTIVE,
            )
            window.alert(userMessage)
            console.log(userMessage)
            return ''
        }
    }

    const saleHandler = async (artData: ArtData, amount: string, expiration: number) => {
        setLoading(true)

        const attemptToSend = async (attempt = 1) => {
            try {
                await axios.post('/sale-nft/', {
                    account: currentAccount,
                    signature: accountSignature,
                    sender: currentAccount,
                    artId: artData.art.id,
                    amount: amount,
                    expirationInHours: expiration,
                })
            } catch (error) {
                if (attempt <= 5) {
                    // Retry up to 5 times
                    console.log(JSON.stringify(error?.response?.data))
                    console.log(`Attempt ${attempt}: Retrying to send ...`)
                    setTimeout(() => attemptToSend(attempt + 1), 2000) // Wait 2 seconds before retrying
                } else {
                    console.error('Failed to send to the server after several attempts.')
                }
            }
        }
        if (artData.state === ArtState.FOR_SALE) {
            await attemptToSend()
        } else if (!(amount === '' || amount === '-1')) {
            const transferResult = await transferNftWithoutTax(artData)
            if (transferResult) {
                await attemptToSend()
            }
        }
        setLoading(false)
    }

    const remove = async (artId: number) => {
        await axios.post('/remove/', {
            account: currentAccount,
            signature: accountSignature,
            artId: artId,
        })
    }

    // Fetch all NFTs users have listed or owned (seller/owner: signer, signer side)
    const fetchPortfolio = async (forced?: boolean): Promise<void> => {
        if (currentAccount && accountSignature) {
            if (forced || !portfolioData) {
                const response = await axios.get('/portfolio/' + currentAccount)
                setPortfolioData(response.data)
            }
        } else {
            setPortfolioData([])
        }
    }

    const fetchProposals = async (forced?: boolean): Promise<void> => {
        if (forced || !proposalData) {
            const response = await axios.get('/proposals/')
            setProposalData(response.data)
        }
    }

    const toggleHideArt = async (artData: ArtData): Promise<void> => {
        if (currentAccount) {
            try {
                const response = await axios.post('/toggle-hide-art/', {
                    account: currentAccount,
                    signature: accountSignature,
                    artId: artData.art.id,
                })
                console.log(response.data)
            } catch (error) {
                console.error('Error toggling art state:', error)
            }
        } else {
            console.warn('No current account available')
        }
    }

    const getTokenomics = (artData: ArtData): Tokenomics => {
        switch (artData.state) {
            case ArtState.CREATED:
            case ArtState.FORCED_AUCTION: {
                const tokenomicsForcedAuction = artData.art.tokenomicsForcedAuction
                return {
                    artState: artData.state,
                    owner: 0, // Owner is the marketplace under Forced Auction
                    fee: tokenomicsForcedAuction.fee,
                    burn: tokenomicsForcedAuction.burn,
                    burnToken: tokenomicsForcedAuction.burnToken,
                    creator: tokenomicsForcedAuction.creator,
                    votes: tokenomicsForcedAuction.votes,
                    claims: tokenomicsForcedAuction.claims,
                    airdrop: tokenomicsForcedAuction.airdrop,
                    treasury: tokenomicsForcedAuction.treasury,
                    burnAddress: tokenomicsForcedAuction.burnAddress,
                    creatorAddress: tokenomicsForcedAuction.creatorAddress,
                    treasuryAddress: tokenomicsForcedAuction.treasuryAddress,
                }
            }
            case ArtState.NOT_FOR_SALE:
            case ArtState.AUCTION:
            case ArtState.FOR_SALE: {
                const tokenomicsAuction = artData.art.tokenomicsAuction
                return {
                    artState: artData.state,
                    owner: tokenomicsAuction.owner,
                    burn: tokenomicsAuction.burn,
                    burnToken: tokenomicsAuction.burnToken,
                    airdrop: tokenomicsAuction.airdrop,
                    claims: tokenomicsAuction.claims,
                    creator: tokenomicsAuction.creator,
                    fee: tokenomicsAuction.fee,
                    treasury: tokenomicsAuction.treasury,
                    votes: 0,
                    burnAddress: tokenomicsAuction.burnAddress,
                    creatorAddress: tokenomicsAuction.creatorAddress,
                    treasuryAddress: tokenomicsAuction.treasuryAddress,
                }
            }
            default:
                return {} as Tokenomics
        }
    }

    const voteHandler = async (artData: ArtData, voteValue: number): Promise<void> => {
        const attemptToSendVote = async (attempt = 1) => {
            try {
                const response = await axios.post('/voting/', {
                    account: currentAccount,
                    signature: accountSignature,
                    userId: currentAccount,
                    artId: artData.art.id,
                    amount: voteValue,
                    tokenId: 0,
                })
                const voteData = response.data
                console.log('Vote submitted: ' + JSON.stringify(voteData))
            } catch (error) {
                if (attempt <= 5) {
                    // Retry up to 5 times
                    console.log(JSON.stringify(error?.response?.data))
                    console.log(`Attempt ${attempt}: Retrying to send vote...`)
                    setTimeout(() => attemptToSendVote(attempt + 1), 2000) // Wait 2 seconds before retrying
                } else {
                    console.error('Error submitting vote:', error)
                }
            }
        }
        await attemptToSendVote()
    }

    const claimHandler = async (castingArtId: number, artData: ArtData, claimValue: number): Promise<void> => {
        const attemptToSendClaim = async (attempt = 1) => {
            try {
                const prepareClaimResponse = await axios.post('/prepare-claim/', {
                    account: currentAccount,
                    signature: accountSignature,
                    userAccount: currentAccount,
                    targetArtId: artData.art.id,
                    amount: claimValue,
                    castingArtId: castingArtId,
                })
                const claimRequestData = prepareClaimResponse.data
                console.log('Prepare Claim Request submitted: ' + JSON.stringify(claimRequestData))
                const transferTx = await proxyTransferToken(
                    claimRequestData.personalWallet,
                    claimRequestData.tokenAddress,
                    claimRequestData.recipient,
                    claimValue.toString(),
                )
                const response = await axios.post('/claiming/', {
                    transactionHash: transferTx.transactionHash,
                    account: currentAccount,
                    signature: accountSignature,
                    userAccount: currentAccount,
                    targetArtId: artData.art.id,
                    amount: claimValue,
                    castingArtId: castingArtId,
                })
                const claimData = response.data
                console.log('Claim submitted: ' + JSON.stringify(claimData))
            } catch (error: any) {
                if (error?.response?.data?.error) {
                    alert(error?.response?.data?.error)
                    return
                }
                if (attempt <= 5) {
                    // Retry up to 5 times
                    console.log(JSON.stringify(error?.response?.data))
                    console.log(`Attempt ${attempt}: Retrying to send claim...`)
                    setTimeout(() => attemptToSendClaim(attempt + 1), 2000) // Wait 2 seconds before retrying
                } else {
                    console.error('Error submitting vote:', error)
                }
            }
        }
        await attemptToSendClaim()
    }

    const proposeHandler = async (artData: ArtData) => {
        const artId = {
            id: artData.art.id.toString(),
        }

        try {
            const response = await axios.post('/propose-mint/', {
                account: currentAccount,
                signature: accountSignature,
                id: artId,
            })
            console.log('Art stored at ' + response.data)
        } catch (error) {
            window.alert(error)
            console.error('Error proposing mint:', error)
        }
    }

    const fetchUpdate = async (update: UpdateType, forced = true): Promise<void> => {
        console.log('Fetching: ', forced + ' ' + update)
        switch (update) {
            case UpdateType.TOKEN: {
                loadTokenAmount().then()
                break
            }
            case UpdateType.VOTES: {
                fetchVotes(forced).then()
                break
            }
            case UpdateType.PROPOSALS: {
                fetchProposals(forced).then()
                break
            }
            case UpdateType.CLAIMS: {
                fetchClaims(forced).then()
                break
            }
            case UpdateType.ADMIN: {
                fetchAdminConfiguration().then()
                break
            }
            case UpdateType.AUCTIONS: {
                fetchAllAuctions(forced).then()
                break
            }
            case UpdateType.PORTFOLIO: {
                fetchPortfolio(forced).then()
                break
            }
            case UpdateType.MARKETS: {
                fetchAllArt(forced).then()
                break
            }
            default:
                return
        }
        setLastFetchTimes((previous) => ({ ...previous, [update]: new Date() }))
    }

    const fetchSpecificUpdate = async (updateSpecific: UpdateSpecific, forced = true): Promise<void> => {
        console.log('Fetching Specific Update: ', forced + ' ' + JSON.stringify(updateSpecific))
        switch (updateSpecific.type) {
            case UpdateSpecificType.TRANSACTION_USER: {
                fetchUserTransactions(updateSpecific.id, forced).then()
                break
            }
            case UpdateSpecificType.TRANSACTION_TOKEN: {
                fetchTokenTransactions(updateSpecific.id, forced).then()
                break
            }
            default:
                return
        }
        setLastFetchTimes((previous) => ({
            ...previous,
            [JSON.stringify(updateSpecific)]: new Date(),
        }))
    }

    useEffect(() => {
        updateTypes.forEach((update) => {
            if (update && activeUpdates.includes(update)) {
                fetchUpdate(update).then()
            }
            setCurrentFetchTimes((previousTimes) => ({
                ...previousTimes,
                [update]: new Date(),
            }))
        })
    }, [updateTypes])

    useEffect(() => {
        specificUpdates.updates.forEach((updateSpecific) => {
            if (
                updateSpecific &&
                activeSpecificUpdates.updates.some((activeUpdate) => _.isEqual(activeUpdate, updateSpecific))
            ) {
                fetchSpecificUpdate(updateSpecific).then()
            }
            setCurrentFetchTimes((previousTimes) => ({
                ...previousTimes,
                [JSON.stringify(updateSpecific)]: new Date(),
            }))
        })
        // Always update client data as it's usually necessary
        loadTokenAmount().then()
        fetchClaims().then()
    }, [specificUpdates])

    useEffect(() => {
        activeUpdates.forEach((update) => {
            const lastFetchTime = lastFetchTimes[update]
            const currentFetchTime = currentFetchTimes[update]
            if (!lastFetchTime || !currentFetchTime || lastFetchTime < currentFetchTime) {
                if (!currentFetchTime) {
                    setCurrentFetchTimes((previousTimes) => ({
                        ...previousTimes,
                        [update]: new Date(),
                    }))
                }
                fetchUpdate(update, true).then()
            } else {
                fetchUpdate(update, false).then()
            }
        })
    }, [activeUpdates])

    useEffect(() => {
        activeSpecificUpdates.updates.forEach((updateSpecific) => {
            const updateSting = JSON.stringify(updateSpecific)
            const lastFetchTime = lastFetchTimes[updateSting]
            const currentFetchTime = currentFetchTimes[updateSting]
            if ((!lastFetchTime && !currentFetchTime) || lastFetchTime < currentFetchTime) {
                if (!currentFetchTime) {
                    setCurrentFetchTimes((previousTimes) => ({
                        ...previousTimes,
                        [JSON.stringify(updateSpecific)]: new Date(),
                    }))
                }
                fetchSpecificUpdate(updateSpecific, true).then()
            } else {
                fetchSpecificUpdate(updateSpecific, false).then()
            }
        })
    }, [activeSpecificUpdates])

    useEffect(() => {
        const wsController = WebSocketController.getInstance()

        // Register message handlers
        wsController.webSocketService.registerHandler<string>('connection', (data: string) => {
            setSocketId(data)
        })

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        wsController.webSocketService.registerHandler<undefined>('pong', (_data: undefined) => {
            // Heartbeat response, no action needed
        })

        wsController.webSocketService.registerHandler<UpdateType[]>('updateTypesResponse', (data: UpdateType[]) => {
            setUpdateTypes(data)
        })

        wsController.webSocketService.registerHandler<WebSocketUpdateResponse>(
            'specificUpdateResponse',
            (data: WebSocketUpdateResponse) => {
                setSpecificUpdates((prevState) => {
                    const newUpdates = data.updates.filter(
                        (newUpdate) =>
                            !prevState.updates.some(
                                (existingUpdate) =>
                                    existingUpdate.id === newUpdate.id && existingUpdate.type === newUpdate.type,
                            ),
                    )
                    return {
                        updates: [...prevState.updates, ...newUpdates],
                    }
                })
            },
        )

        wsController.webSocketService.registerHandler<ChatMessage[]>('messagesResponse', (data: ChatMessage[]) => {
            setMessages(data)
        })

        wsController.webSocketService.registerHandler<ChatMessage>('messageResponse', (data: ChatMessage) => {
            setMessages((prevMessages) => {
                const exists = prevMessages.some((msg) => msg.id === data.id)
                if (!exists) {
                    return [...prevMessages, data]
                }
                return prevMessages
            })
        })

        wsController.webSocketService.registerHandler<TypingResponse>('typingResponse', (data: TypingResponse) => {
            setTypingStatus(`${data.userName}`)
        })

        wsController.webSocketService.registerHandler<number>('pulsePrice', (data: number) => {
            setPulsePrice(data.toString())
        })

        wsController.webSocketService.registerHandler<number>('crazyPrice', (data: number) => {
            setCrazyPrice(data.toString())
        })

        let active = true
        const refreshPrices = () => {
            if (!active || !wsController.isConnected()) return
            wsController.send({ type: 'getPulsePrice' })
            wsController.send({ type: 'getCrazyPrice' })
        }
        const refreshVisiblePrices = () => {
            if (document.visibilityState === 'visible') refreshPrices()
        }

        // Poll through the connected socket, including after it reconnects.
        const priceRefreshInterval = window.setInterval(refreshPrices, 30_000)
        document.addEventListener('visibilitychange', refreshVisiblePrices)
        window.addEventListener('online', refreshPrices)

        // Connect and send initial messages
        wsController.connect()

        wsController.webSocketService.onOpen(() => {
            if (!active) return
            wsController.send({ type: 'getAllMessages' })
            refreshPrices()
        })

        return () => {
            active = false
            window.clearInterval(priceRefreshInterval)
            document.removeEventListener('visibilitychange', refreshVisiblePrices)
            window.removeEventListener('online', refreshPrices)
            wsController.disconnect()
        }
    }, [])

    useEffect(() => {
        fetchCategories().catch(console.error)
    }, [])

    // Auto connect available accounts on load
    useEffect(() => {
        if (accountSignature?.length) {
            fetchPortfolio(true).catch(console.error)
            fetchVotes(true).catch(console.error)
            fetchClaims(true).catch(console.error)
        }
    }, [accountSignature])

    useEffect(() => {
        handleUpdateUser().catch(console.error)
    }, [currentAccount, tokenAmount])

    useEffect(() => {
        fetchAdminConfiguration().catch(console.error)
    }, [artDataList])

    useEffect(() => {
        setPortfolioClaimTable(generatePortfolioClaimTable(portfolioData))
    }, [portfolioData])

    useEffect(() => {
        setClaimedAmountTable(generateClaimedAmountTable(claimedDataList))
    }, [claimedDataList])

    useEffect(() => {
        if (!currentAccount || currentAccount === '' || accountSignature === '') {
            setVotePower('-')
            setTotalClaimPower('-')
        } else {
            const totalVotedAmount: number = parseFloat(tokenAmount) - calcTotalVotedAmount(votedDataList)
            if (totalVotedAmount <= 0) {
                setVotePower('0')
            } else if (!totalVotedAmount) {
                setVotePower('-')
            } else {
                setVotePower(parseFloat(totalVotedAmount.toString()).toFixed(2))
            }

            const claimPower: number = Object.values(portfolioClaimTable).reduce((total, amount) => total + amount, 0)
            if (claimPower && claimPower <= 0) {
                setTotalClaimPower('0')
            } else if (!claimPower) {
                setTotalClaimPower('-')
            } else {
                setTotalClaimPower(parseFloat(claimPower.toString()).toFixed(2))
            }
        }
    }, [votedDataList, portfolioClaimTable, tokenAmount, currentAccount])

    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('filters', JSON.stringify(filters))
        }
    }, [filters])

    // Auto connect available accounts on load
    useEffect(() => {
        if (!wallet) {
            setAccountSignature('')
            return
        }
        if (!account?.length) {
            setAccountSignature('')
            return
        }

        if (currentAccount && account !== currentAccount) {
            walletController()
                .disconnectWallet()
                .then(() => {})
            return
        }

        setWalletProvider(wallet.provider)

        localStorage.setItem('account', account)

        const localSignature = localStorage.getItem(`account_signature_${account}`)

        if (localSignature && account?.length) {
            axios
                .post('/check-account-signature/', {
                    account: account,
                    signature: localSignature,
                })
                .then((result) => {
                    if (result.status === 200 && result.data.valid) {
                        setAccountSignature(localSignature)
                        setCurrentAccount(account)
                    } else {
                        localStorage.removeItem(`account_signature_${account}`)
                        setCurrentAccount('')
                        setAccountSignature('')
                        walletController().renewAccountSignature(wallet).then()
                    }
                })
        } else if (account?.length) {
            walletController().renewAccountSignature(wallet).then()
        }
    }, [wallet])

    // Load token amount once the account is connected
    useEffect(() => {
        if (currentAccount && account === currentAccount) {
            if (isConnected && currentAccount && accountSignature) {
                loadTokenAmount().then()
            }
        }
    }, [isConnected, currentAccount, accountSignature, loadTokenAmount])

    return (
        <Context.Provider
            value={{
                acceptBid,
                acceptOffer,
                adminConfiguration,
                artDataList,
                auctionDataList,
                accountSignature,
                bidHandler,
                categories,
                claimedAmountTable,
                claimedDataList,
                claimHandler,
                crazyPrice,
                currentAccount,
                resetWallet,
                endAuction,
                faucetHandler,
                fetchImage,
                filters,
                getCachedImage,
                getTokenomics,
                hideContent,
                isConnected,
                loadPulseAmount,
                loadTokenAmount,
                loading,
                logIn,
                messages,
                offerHandler,
                portfolioClaimTable,
                portfolioData,
                proposeHandler,
                proposalData,
                proxyTransferToken,
                proxyTransferNative,
                proxyTransferNft,
                pulseAmount,
                pulsePrice,
                remove,
                saleHandler,
                setActiveUpdates,
                setActiveSpecificUpdates,
                setHideContent,
                setLoading,
                socketId,
                tokenAmount,
                toggleHideArt,
                toggleFilter,
                totalClaimPower,
                transactionUserArray,
                transactionTokenArray,
                transferClaim,
                transferNft,
                typingStatus,
                walletController,
                voteHandler,
                votePower,
                votedDataList,
                walletProvider,
                webSocketService,
            }}
        >
            {children}
        </Context.Provider>
    )
}

export default ContextProvider
